import * as React from "react";
import { cn } from "@shared/utils";
import { Button } from "../buttons";
import { useErrorHandling } from "../../utils/useErrorHandling";
import type { FileUploadProps } from "../../types/FileUploadProps";

/**
 * @group Components
 * @summary <blockquote>FileUpload component for file selection and upload.</blockquote>
 * @example
 * ```tsx
 * <FileUpload 
 *   onChange={(file) => setFile(file)} 
 *   accept="image/*" 
 *   maxSize={1024 * 1024} 
 * />
 * ```
 */
export function FileUpload({
  value,
  onChange,
  accept,
  className,
  buttonText = "Choose File",
  error,
  helperText,
  disabled,
  maxSize,
  onUpload,
  ...props
}: FileUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { showErrorToast } = useErrorHandling();

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file && maxSize && file.size > maxSize) {
      onChange(null);
      return;
    }
    onChange(file);
    if (file && onUpload) {
      onUpload(file).catch((error) => {
        showErrorToast(error, 'File Upload Error');
      });
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          className="hidden"
          disabled={disabled}
          {...props}
        />
        <Button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          variant={error ? "destructive" : "default"}
        >
          {buttonText}
        </Button>
        {value && (
          <span className="text-sm text-muted-foreground">
            {value.name}
          </span>
        )}
      </div>
      {helperText && (
        <span
          className={cn(
            "text-sm",
            error ? "text-destructive" : "text-muted-foreground"
          )}
        >
          {helperText}
        </span>
      )}
    </div>
  );
}
